/** «Рассылки» → WABA — ровно то, что отдаёт `GET /api/mailing/waba`. */
import { apiFetch, query } from "@/lib/api";

/** Что человек сделал с сообщением: кнопка сильнее текста, текст сильнее галочек. */
export type WabaStep = "failed" | "unread" | "read" | "talk" | "yes" | "no";
/** Итог — один: «записались», иначе шаг, на котором потеряли. Сумма = отправлено. */
export type WabaResult = "booked" | WabaStep;

export type WabaRow = {
  guid: string;
  phone: string;
  /** Чат в веб-интерфейсе Wazzup. */
  chat_url: string | null;
  sent: string;
  /** «Лид 1» — рассылка CRM новому лиду; «Дожим» — наш текст молчащему. */
  template: string;
  status: "sent" | "delivered" | "read" | "error" | string;
  error: string;
  step: WabaStep;
  result: WabaResult;
  button: string | null;
  /** Клиент написал текстом (в том числе после кнопки). */
  talk: boolean;
  talk_count: number;
  first_text: string | null;
  replied: string | null;
  /** Когда после отправки сделка номера перешла в «ждем клиента». */
  booked: string | null;
  /** После отправки: первый приход в салон по сделке номера и договор в «Бронь»/«Подписан». */
  came: string | null;
  bron: string | null;
  /** Последний лид номера — текущее состояние. */
  lead: { id: number; status: string; name: string } | null;
  leads: number;
  /** Последняя сделка лидов номера — текущая стадия. */
  deal: { id: number; stage: string; name: string } | null;
  /** Комментарии сотрудников к номеру, по порядку. */
  comments: WabaComment[];
};

export type WabaComment = { id: number; at: string; user_id: number; user: string; text: string };

export type Waba = {
  /** Окно «последние N часов» или один день (`day`) — одно из двух. */
  hours: number | null;
  day: string | null;
  since: string;
  until: string;
  total: number;
  results: Record<WabaResult, number>;
  /** Что записавшиеся сделали с сообщением до записи. */
  booked_by: Record<WabaStep, number>;
  templates: Record<string, number>;
  rows: WabaRow[];
};

/** За последние часы, за день `day` или за дни `day`…`to` включительно. */
export function loadWaba(hours: number, day?: string | null, to?: string | null): Promise<Waba> {
  return apiFetch<Waba>(`/api/mailing/waba${query(day ? { day, ...(to ? { to } : {}) } : { hours: String(hours) })}`);
}

export type WabaGroup = "day" | "week";

/** Строка «По дням»: воронка отправок дня или недели `from`…`to`; сумма `results` = `total`. */
export type WabaDay = {
  date: string;
  from: string;
  to: string;
  total: number;
  results: Record<WabaResult, number>;
  /** Дальше записи, по истории после отправки: пришли в салон, забронировали. */
  came: number;
  bron: number;
  templates: Record<string, number>;
};

export type WabaDays = {
  from: string;
  to: string;
  group: WabaGroup;
  since: string;
  until: string;
  total: number;
  results: Record<WabaResult, number>;
  came: number;
  bron: number;
  rows: WabaDay[];
};

export function loadWabaDays(from: string, to: string, group: WabaGroup): Promise<WabaDays> {
  return apiFetch<WabaDays>(`/api/mailing/waba/days${query({ from, to, group })}`);
}

/** Добавить комментарий к номеру; ответ — все комментарии номера. */
export function addWabaComment(phone: string, text: string): Promise<WabaComment[]> {
  return apiFetch<WabaComment[]>("/api/mailing/waba/comment", { method: "POST", body: JSON.stringify({ phone, text }) });
}

/** Сообщение переписки номера — `GET /api/mailing/waba/chat`. */
export type WabaMessage = {
  guid: string;
  at: string | null;
  out: boolean;
  type: string;
  /** Вид нашего шаблона («Лид 1», «Дожим», «Шаблон»); у прочих — null. */
  template: string | null;
  /** Клиент нажал кнопку шаблона («Актуально» / «Не актуально»). */
  button: boolean;
  text: string;
  uri: string | null;
  author: string | null;
  /** Галочки нашего сообщения: sent · delivered · read · error. */
  status: string | null;
  error: string | null;
};

export function loadWabaChat(phone: string): Promise<{ phone: string; messages: WabaMessage[] }> {
  return apiFetch(`/api/mailing/waba/chat${query({ phone })}`);
}
